import styled from '@emotion/styled';
import { designSystem } from '@/styles/theme';

/**
 * 세미나 등록·수정 폼 공용 스타일. SeminarForm 과 하위 입력 부품(단계 카드·회차·접속·썸네일)이 같이 쓴다.
 * 2026-09-29 폼 개편(docs/seminar-form-redesign-plan.md, 시안 formmock): 52px 상자형 입력, 알약 칩, 큰 선택은 카드.
 */

/** 시안 색 — 폼 안에서만 쓴다 */
export const FORM_COLOR = {
  text: '#1F2A28',
  sub: '#4F5B58',
  muted: '#66736F',
  placeholder: '#9AA5A2',
  border: '#D3DBD8',
  line: '#E3E8E6',
  lineSoft: '#F0F3F2',
  bg: '#F5F7F6',
  primary: designSystem.colors.primary, // #5A9B96
  primaryDeep: '#2F6B66',
  primarySoft: '#E6F1EF',
  primaryCard: '#F1F8F6',
  primaryDisabled: '#B8CFCB',
  danger: '#E11D48',
  dangerText: '#E11D48',
  error: '#E11D48',
} as const;

/** 한 칸 묶음(라벨 + 입력 + 오류) */
export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

/** 단계 안의 한 항목 — 항목끼리 22px 띄운다 */
export const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  margin-bottom: 22px;

  &:last-child {
    margin-bottom: 0;
  }
`;

/** 항목 이름. 필수 표시는 <span>*</span>, 오른쪽 짧은 안내는 <small> */
export const Label = styled.label`
  display: flex;
  align-items: baseline;
  gap: 3px;
  font-size: 14px;
  font-weight: 700;
  color: ${FORM_COLOR.text};

  > span {
    color: ${FORM_COLOR.danger};
  }

  small {
    margin-left: auto;
    font-size: 12.5px;
    font-weight: 500;
    color: ${FORM_COLOR.muted};
  }
`;

export const HelpText = styled.p`
  font-size: 0.85rem;
  color: ${FORM_COLOR.muted};
  margin: 0 0 8px 0;
`;

/** 칸 아래 짧은 안내 — 문장마다 줄바꿈(\n)을 그대로 보여 준다 */
export const GuideLines = styled.p`
  font-size: 13px;
  line-height: 1.55;
  color: ${FORM_COLOR.muted};
  margin: 0;
  white-space: pre-line;
  word-break: keep-all;

  b {
    color: ${FORM_COLOR.text};
    font-weight: 700;
  }
`;

/** 노란 고지 상자 (참가자 공개 범위·폐강 환불 등 꼭 읽어야 하는 문구) */
export const NoticeBox = styled.div<{ tone?: 'warning' | 'danger' }>`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ tone }) => (tone === 'danger' ? '#fef2f2' : '#fffbeb')};
  color: ${({ tone }) => (tone === 'danger' ? '#b91c1c' : '#92400e')};
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-line;
  word-break: keep-all;

  svg {
    flex: 0 0 auto;
    font-size: 18px;
    margin-top: 1px;
  }
`;

/** 52px 상자형 입력 */
export const StyledInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: 52px;
  padding: 0 14px;
  border: 1px solid ${(props) => (props.hasError ? FORM_COLOR.error : FORM_COLOR.border)};
  border-radius: 12px;
  font-size: 15px;
  /* 폰은 16px — 아이폰이 16px 미만 입력칸을 누르면 화면을 확대한다(UI 가이드 7.1) */
  @media (max-width: 768px) {
    font-size: 16px;
  }
  font-family: inherit;
  color: ${FORM_COLOR.text};
  background-color: #fff;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
  min-width: 0;
  -webkit-appearance: none;
  appearance: none;

  &:focus {
    border-color: ${(props) => (props.hasError ? FORM_COLOR.error : FORM_COLOR.primary)};
  }

  &::placeholder {
    color: ${FORM_COLOR.placeholder};
  }

  &:disabled {
    background-color: #f3f5f4;
    color: ${FORM_COLOR.muted};
  }

  /* iOS 날짜·시각 입력은 글자가 가운데로 가고 높이가 줄어든다 — 왼쪽 정렬·높이 고정 */
  &[type='date'],
  &[type='time'] {
    display: flex;
    align-items: center;
    line-height: 52px;
  }

  &::-webkit-date-and-time-value {
    text-align: left;
  }
`;

export const StyledTextarea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  min-height: 96px;
  padding: 14px;
  border: 1px solid ${(props) => (props.hasError ? FORM_COLOR.error : FORM_COLOR.border)};
  border-radius: 12px;
  font-size: 15px;
  /* 폰은 16px — 아이폰이 16px 미만 입력칸을 누르면 화면을 확대한다(UI 가이드 7.1) */
  @media (max-width: 768px) {
    font-size: 16px;
  }
  font-family: inherit;
  line-height: 1.5;
  color: ${FORM_COLOR.text};
  background: #fff;
  resize: vertical;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${(props) => (props.hasError ? FORM_COLOR.error : FORM_COLOR.primary)};
  }

  &::placeholder {
    color: ${FORM_COLOR.placeholder};
  }
`;

export const ErrorMessage = styled.p`
  font-size: 13px;
  color: ${FORM_COLOR.error};
  margin: 0;
  white-space: pre-line;
  word-break: keep-all;
`;

export const SectionDivider = styled.div`
  height: 1px;
  background-color: ${FORM_COLOR.lineSoft};
  margin: 8px 0;
`;

export const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 800;
  color: ${FORM_COLOR.text};
  margin: 8px 0 0 0;
`;

export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  min-width: 0;
`;

/** 알약 칩 묶음 */
export const TagSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 6px;
`;

/** 알약 칩(36px) — 선택 시 연청록 바탕·청록 테두리 */
export const TagOption = styled.button<{ selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid ${(props) => (props.selected ? FORM_COLOR.primary : FORM_COLOR.border)};
  background: ${(props) => (props.selected ? FORM_COLOR.primarySoft : '#fff')};
  color: ${(props) => (props.selected ? FORM_COLOR.primaryDeep : FORM_COLOR.text)};
  border-radius: 18px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: ${(props) => (props.selected ? 700 : 500)};
  white-space: nowrap;

  svg {
    width: 16px;
    height: 16px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

/** 큰 선택 카드 묶음 — 2칸(유형) 또는 3칸(진행 방식) */
export const ChoiceCardGroup = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns ?? 2}, minmax(0, 1fr));
  gap: 8px;
`;

/** 큰 선택 카드 — 선택 시 2px 청록 테두리 */
export const ChoiceCard = styled.button<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  min-width: 0;
  padding: ${({ selected }) => (selected ? '13px' : '14px')};
  border: ${({ selected }) => (selected ? `2px solid ${FORM_COLOR.primary}` : `1px solid ${FORM_COLOR.border}`)};
  border-radius: 14px;
  background: ${({ selected }) => (selected ? FORM_COLOR.primaryCard : '#fff')};
  text-align: left;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.45;
  color: ${FORM_COLOR.sub};
  word-break: keep-all;
  cursor: pointer;

  b {
    font-size: 15px;
    font-weight: 800;
    color: ${({ selected }) => (selected ? FORM_COLOR.primaryDeep : FORM_COLOR.text)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: ${({ selected }) => (selected ? 0.7 : 0.45)};
  }
`;

/** 입력 오른쪽에 단위(원·명)를 붙이는 틀 */
export const UnitInput = styled.div`
  position: relative;
  min-width: 0;

  input {
    padding-right: 44px;
  }

  > em {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-style: normal;
    font-size: 15px;
    font-weight: 700;
    color: ${FORM_COLOR.sub};
    pointer-events: none;
  }
`;

/** 스위치가 달린 52px 줄 (인원 제한 없음 등) */
export const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 52px;
  padding: 0 6px 0 14px;
  border: 1px solid ${FORM_COLOR.border};
  border-radius: 12px;
  background: #fff;
  font-size: 15px;
  color: ${FORM_COLOR.text};
  cursor: pointer;
`;

/** 정산 예상 계산 상자 */
export const CalcBox = styled.div`
  padding: 14px;
  border: 1px solid ${FORM_COLOR.line};
  border-radius: 14px;
  background: #fff;
  font-size: 14px;

  > div {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 0;
    color: ${FORM_COLOR.sub};
  }

  > div.total {
    margin-top: 6px;
    padding-top: 10px;
    border-top: 1px dashed ${FORM_COLOR.border};
    font-size: 15px;
    font-weight: 800;
    color: ${FORM_COLOR.text};
  }
`;

/** 제목 옆 글자 수 */
export const CharCounter = styled.span<{ over?: boolean }>`
  align-self: flex-end;
  font-size: 12.5px;
  font-weight: 500;
  color: ${({ over }) => (over ? FORM_COLOR.error : FORM_COLOR.muted)};
`;
